/**
 * FiCostBreakdownChart — horizontal bar list of cost by vendor, colored
 * via vendorColor() and tagged Variable/Fijo depending on cost shape.
 */
import { useMemo } from 'react'
import type { CostBreakdownItem } from './fiTypes'
import { formatCurrency, vendorColor } from './fiFormat'

interface FiCostBreakdownChartProps {
  costBreakdown: CostBreakdownItem[]
}

export default function FiCostBreakdownChart({ costBreakdown }: FiCostBreakdownChartProps) {
  const sorted = useMemo(
    () => [...costBreakdown].sort((a, b) => b.cost - a.cost),
    [costBreakdown]
  )
  const maxCost = Math.max(...sorted.map(c => c.cost), 1)
  const total = sorted.reduce((s, c) => s + c.cost, 0)

  return (
    <div className="bg-gray-800 rounded-xl p-6">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-white">Costo por Proveedor</h3>
          <p className="text-gray-400 text-sm">COGS del período, ordenado de mayor a menor</p>
        </div>
        <span className="text-white font-bold">{formatCurrency(total)}</span>
      </div>

      {sorted.length === 0 ? (
        <div className="text-center py-8 text-gray-400">Sin costos configurados aún.</div>
      ) : (
        <div className="space-y-3">
          {sorted.map(item => {
            const pct = total > 0 ? (item.cost / total) * 100 : 0
            const widthPct = (item.cost / maxCost) * 100
            const badgeLabel = item.costType === 'overhead'
              ? 'Overhead'
              : item.isFixed ? 'Fijo' : item.costType === 'mixed' ? 'Mixto' : 'Variable'
            const badgeTone = item.isFixed
              ? 'bg-slate-500/20 text-slate-300'
              : 'bg-amber-500/20 text-amber-300'
            return (
              <div key={item.vendor}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: vendorColor(item.vendor) }} />
                    <span className="text-white text-sm font-medium">{item.vendor}</span>
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide ${badgeTone}`}>
                      {badgeLabel}
                    </span>
                  </div>
                  <div className="text-right">
                    <span className="text-white text-sm font-semibold">{formatCurrency(item.cost)}</span>
                    <span className="text-gray-500 text-xs ml-2">{pct.toFixed(1)}%</span>
                  </div>
                </div>
                <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{ width: `${widthPct}%`, backgroundColor: vendorColor(item.vendor) }}
                  />
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
